/**
 * Maps Gobodo/Stripe API invoice shape to app Invoice and InvoiceLineItem.
 */

import type { Invoice, InvoiceLineItem } from '../types/index';

const CENTS_TO_DOLLARS = 1 / 100;

function unixToIso(unix: number | undefined | null): string {
  if (unix == null || typeof unix !== 'number') return new Date().toISOString().split('T')[0];
  const d = new Date(unix * 1000);
  return d.toISOString().split('T')[0];
}

function mapStripeStatusToApp(status: string | undefined | null, dueDateUnix?: number | null): Invoice['status'] {
  const s = (status ?? '').toLowerCase();
  if (s === 'draft') return 'draft';
  if (s === 'paid') return 'paid';
  if (s === 'void') return 'cancelled';
  if (s === 'uncollectible') return 'cancelled';
  if (s === 'open') {
    if (dueDateUnix != null && typeof dueDateUnix === 'number') {
      const due = new Date(dueDateUnix * 1000);
      if (due < new Date()) return 'overdue';
    }
    return 'sent';
  }
  return 'draft';
}

function getLineData(invoice: any): any[] {
  const lines = invoice?.lines ?? invoice?.getLines?.();
  if (!lines) return [];
  const data = lines?.data ?? lines?.getData?.();
  return Array.isArray(data) ? data : [];
}

export function stripeLineToAppLineItem(line: any): InvoiceLineItem {
  const id = line?.id ?? line?.getId?.() ?? `li_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
  const amount = line?.amount ?? line?.getAmount?.() ?? 0;
  const quantity = Math.max(0, Number(line?.quantity ?? line?.getQuantity?.() ?? 1));
  const amountNum = typeof amount === 'number' ? amount : Number(amount);
  const amountDollars = (Number.isNaN(amountNum) ? 0 : amountNum) * CENTS_TO_DOLLARS;
  const unitPrice = quantity > 0 ? amountDollars / quantity : 0;
  const description = line?.description ?? line?.getDescription?.() ?? '';
  const productRaw = line?.price?.product ?? line?.product ?? line?.price?.id ?? '';
  const productId = typeof productRaw === 'object' && productRaw != null && 'id' in productRaw
    ? String((productRaw as { id?: string }).id ?? '')
    : String(productRaw ?? '');
  const productName = description || productId || 'Line Item';
  return {
    id: String(id),
    productId,
    productName: String(productName ?? ''),
    productCode: '',
    quantity,
    unitPrice,
    lineTotal: amountDollars,
    description: description || undefined,
  };
}

export interface MapStripeInvoiceOptions {
  clientId?: string;
  clientName?: string;
}

/**
 * Maps a Gobodo/Stripe API invoice object to the app Invoice type.
 * Stripe amounts are in cents; we convert to dollars.
 */
export function stripeInvoiceToAppInvoice(apiInvoice: any, options?: MapStripeInvoiceOptions): Invoice {
  const inv = apiInvoice ?? {};
  const id = inv.id ?? inv.getId?.() ?? inv.invoice_id ?? '';
  const number = inv.number ?? inv.getNumber?.() ?? inv.invoice_number ?? `INV-${String(id).slice(-8)}`;
  const statusRaw = inv.status ?? inv.getStatus?.();
  const dueDateUnix = inv.dueDate ?? inv.getDueDate?.() ?? inv.due_date;
  const status = mapStripeStatusToApp(statusRaw, dueDateUnix);
  const createdUnix = inv.created ?? inv.getCreated?.();
  const subtotal = (inv.subtotal ?? inv.getSubtotal?.() ?? 0) * CENTS_TO_DOLLARS;
  const tax = (inv.tax ?? inv.getTax?.() ?? 0) * CENTS_TO_DOLLARS;
  const total = (inv.total ?? inv.getTotal?.() ?? 0) * CENTS_TO_DOLLARS;
  const currency = (inv.currency ?? inv.getCurrency?.() ?? 'USD').toString().toUpperCase();
  const hostedUrl = inv.hostedInvoiceUrl ?? inv.getHostedInvoiceUrl?.() ?? inv.host_invoice_url ?? inv.invoice_url;
  const customerName = inv.customerName ?? inv.getCustomerName?.() ?? inv.customer_name ?? '';

  const statusTransitions = inv.statusTransitions ?? inv.getStatusTransitions?.() ?? inv.status_transitions;
  const paidAt = statusTransitions?.paid_at ?? statusTransitions?.paidAt;
  const paidDate = status === 'paid' && paidAt != null ? unixToIso(typeof paidAt === 'number' ? paidAt : paidAt) : undefined;

  const lineData = getLineData(inv);
  const lineItems: InvoiceLineItem[] = lineData.map((line: any) => stripeLineToAppLineItem(line));
  if (lineItems.length === 0) {
    lineItems.push({
      id: `empty_${id}`,
      productId: '',
      productName: 'No line items',
      productCode: '',
      quantity: 0,
      unitPrice: 0,
      lineTotal: 0,
    });
  }

  const now = new Date().toISOString();
  return {
    id: String(id),
    invoiceNumber: String(number),
    clientId: options?.clientId ?? '',
    clientName: options?.clientName ?? customerName ?? '',
    status,
    issueDate: unixToIso(createdUnix),
    dueDate: unixToIso(dueDateUnix),
    paidDate,
    subtotal,
    taxAmount: tax,
    totalAmount: total,
    currency,
    paymentLink: typeof hostedUrl === 'string' ? hostedUrl : undefined,
    stripeInvoiceId: String(id),
    notes: inv.description ?? inv.getDescription?.() ?? undefined,
    lineItems,
    createdDate: unixToIso(createdUnix),
    createdBy: 'system',
    lastModifiedDate: now,
  };
}
